import axios from 'axios';
import CryptoJS from 'crypto-js';
import * as demoData from './demoData';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const DYNAMIC_KEY_SALT = process.env.REACT_APP_DYNAMIC_KEY_SALT;

// ?demo=true loads the site entirely from local mock data (src/services/demoData.js)
// instead of calling the real API - used for admin-side demo/preview links.
// ?theme=themeN picks which theme renders in demo mode (defaults to theme1).
// Read once at load and cached: client-side route changes (React Router's
// <Link>) only touch the pathname, so re-reading window.location.search on
// every call would silently drop demo mode after the first navigation.
const initialParams = new URLSearchParams(window.location.search);
const DEMO_MODE = ['1', 'true', 'yes'].includes((initialParams.get('demo') || '').toLowerCase());
const DEMO_THEME = initialParams.get('theme') || 'theme1';

function isDemoMode() {
  return DEMO_MODE;
}

function getDemoTheme() {
  return DEMO_THEME;
}

// Used by PreserveDemoQuery to re-append ?demo=...&theme=... to the URL bar
// after client-side navigations, which otherwise strip the query string.
export function getDemoQueryString() {
  if (!DEMO_MODE) return null;
  return `demo=true&theme=${DEMO_THEME}`;
}

function getDemoGetResponse(url) {
  if (url === '/navigation') return demoData.navigation;
  if (url === '/sliders') return demoData.sliders;
  if (url === '/inventory/filters') return demoData.filterOptions;
  if (url === '/vehicles/featured') return demoData.featuredVehicles;
  if (url === '/vehicles') return demoData.vehicles;
  if (url === '/why-buy') return demoData.whyBuyFeatures;
  if (url === '/contact') return demoData.contact;
  if (url === '/social-links') return demoData.socialLinks;
  if (url === '/business-hours') return demoData.businessHours;
  if (url === '/contact-form') return demoData.contactForm;
  if (url.startsWith('/menu/')) return demoData.pageContent;
  if (url.startsWith('/inventory/')) {
    const id = url.slice('/inventory/'.length);
    return demoData.vehicles.find((v) => String(v.id) === id) || demoData.vehicles[0];
  }
  return null;
}

// Mirrors the backend's expected hash: sha256(gmdate('Y-m-d-H') . salt).
// Recomputed per-request so it stays valid across the hour boundary.
function getDynamicKey() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  const h = String(now.getUTCHours()).padStart(2, '0');
  const dateHour = `${y}-${m}-${d}-${h}`;
  return CryptoJS.SHA256(dateHour + DYNAMIC_KEY_SALT).toString(CryptoJS.enc.Hex);
}

// Populated at app startup from getStartPoint(), which resolves the dealer
// user id, website id, and approval status for the site currently being
// served (see App.js).
const siteConfig = {
  userId: null,
  websiteId: null,
  requestStatus: null,
  isPublished: null,
};

// Reads ?preview=1&website_id=X from the current URL (used by the dealer admin's
// "Preview" button to show draft/unpublished content). Both must be present -
// otherwise the default website_id keeps being used.
function getPreviewOverrides() {
  const params = new URLSearchParams(window.location.search);
  const isPreview = ['1', 'true', 'yes'].includes((params.get('preview') || '').toLowerCase());
  const websiteId = params.get('website_id');

  return isPreview && websiteId ? { preview: 1, website_id: websiteId } : null;
}

export class ServiceWrapper {

  get defaultParams() {
    return {
      user_id: siteConfig.userId,
      website_id: siteConfig.websiteId,
      ...getPreviewOverrides(),
    };
  }

  // Resolves the dealer user id and website id for the current host. Called
  // once at app startup, before any other request needs those defaults.
  getStartPoint = () => {
    if (isDemoMode()) {
      return Promise.resolve({ ...demoData.startPoint, theme: getDemoTheme() });
    }
    return axios.get(`${API_BASE_URL}/start-point`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        'x-dynamic-key': getDynamicKey(),
      },
      params: { slug: window.location.host },
    }).then((r) => r.data);
  };

  setSiteConfig = ({ user_id, website }) => {
    siteConfig.userId = user_id;
    siteConfig.websiteId = website?.id;
    siteConfig.requestStatus = website?.request_status;
    siteConfig.isPublished = website?.is_published;
  };

  isApproved = () => siteConfig.requestStatus === 'approved';

  isPublished = () => Boolean(siteConfig.isPublished);

  // Every API call (other than getStartPoint, which is what determines this
  // status in the first place) is gated on the site's approval/publish status
  // so an unapproved or unpublished site can't pull data through any endpoint.
  callGet = async (url, params = {}) => {
    if (!this.isApproved()) {
      return Promise.reject(new Error('Site request is not approved'));
    }
    if (!this.isPublished()) {
      return Promise.reject(new Error('Site is not published'));
    }
    if (isDemoMode()) {
      return { data: getDemoGetResponse(url) };
    }
    return await axios.get(API_BASE_URL + url, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        'x-dynamic-key': getDynamicKey(),
      },
      params: { ...this.defaultParams, ...params },
    });
  };

  callPost = async (url, body) => {
    if (!this.isApproved()) {
      return Promise.reject(new Error('Site request is not approved'));
    }
    if (!this.isPublished()) {
      return Promise.reject(new Error('Site is not published'));
    }
    if (isDemoMode()) {
      return { data: { success: true } };
    }
    return await axios.post(API_BASE_URL + url, body, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        'x-dynamic-key': getDynamicKey(),
      },
    });
  };

  // ─── API methods ────────────────────────────────────────────────────────────

  getNavigation = () =>
    this.callGet('/navigation').then((r) => r.data);

  getSliders = () =>
    this.callGet('/sliders').then((r) => r.data);

  getFilterOptions = () =>
    this.callGet('/inventory/filters').then((r) => r.data);

  getFeaturedVehicles = () =>
    this.callGet('/vehicles/featured').then((r) => r.data);

  getVehicles = async (filters = {}) => {
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await this.callGet('/vehicles', activeFilters);
    return Array.isArray(data)
      ? data.map((v) => ({
          ...v,
          bodyType: v.bodyType || v.bodyStyle,
          sellPrice: v.sellPrice || v.sell_price,
        }))
      : data;
  };

  getWhyBuyFeatures = () =>
    this.callGet('/why-buy').then((r) => r.data);

  getContact = () =>
    this.callGet('/contact').then((r) => r.data);

  getSocialLinks = () =>
    this.callGet('/social-links').then((r) => r.data);

  getBusinessHours = () =>
    this.callGet('/business-hours').then((r) => r.data);

  getPageContent = (id) =>
    this.callGet(`/menu/${id}`).then((r) => r.data);

  getInventoryItem = (id) =>
    this.callGet(`/inventory/${id}`).then((r) => {
      const v = r.data?.data ?? r.data;
      return {
        ...v,
        bodyType: v.bodyType || v.bodyStyle,
        sellPrice: v.sellPrice || v.sell_price,
      };
    });

  getContactForm = () =>
    this.callGet('/contact-form').then((r) => r.data);

  submitLoanApplication = (fields) =>
    this.callPost('/loan-application/submit', {
      user_id: siteConfig.userId,
      website_domain: window.location.hostname,
      ...fields,
    }).then((r) => r.data);

  submitContactQuery = (fields) =>
    this.callPost('/contact-form/submit', {
      user_id: siteConfig.userId,
      website_domain: window.location.hostname,
      fields,
    }).then((r) => r.data);
}

export const apiService = new ServiceWrapper();
